// Issue 1:
// This base class was created to solve an issue where the $scope wasn't available in the injector,
// when an UpgradeComponent component is loaded dynamically either directly as an entryComponent for an NgModule,
// or inside of an entry component for an NgModule.
// The error was something like this: Error: StaticInjectorError(AppModule)[$scope].
// See this angular issue for more explanation: https://github.com/angular/angular/issues/27432.
// Although it's also possible there could be other ways that we may be loading components dynamically in the future,
// those cases can be handled at that time (or they may already work with this logic).

// The specific use-case for these UpgradeComponents getting loaded dynamically are for modals that are being shown
// via ngx-bootstrap.

// IMPORTANT:
// For now, this class should be used as a base class for every UpgradeComponent,
// in case they are ever used inside of a modal component, either directly or indirectly.

// Issue 2:
// The other issue is that even when the $scope was getting injected properly, there was no digest cycle happening
// after the component was created via ngx-bootstrap, so the view of the UpgradeComponent did not have updated bindings, etc.
// See this question for details: https://stackoverflow.com/q/58986644/5335355.

import { DialogRef } from '@angular/cdk/dialog';
import {UpgradeComponent} from '@angular/upgrade/static';

import {
  ElementRef,
  Injector,
} from '@angular/core';

import { ApDialogRef } from '../modals/index';

import {IScope} from 'angular';

const $SCOPE = '$scope';

export class ApUpgradeComponent extends UpgradeComponent {

  constructor(name: string, elementRef: ElementRef, injector: Injector) {
    const modalOptions = (injector.get(DialogRef, null) as ApDialogRef | null)?.config;

    // If there are ModalOptions with a modal elementInjector,
    // then this component is dynamically loaded inside of a modal.
    // So for this UpgradeComponent to work properly, we need to use the elementInjector,
    // instead of the module injector that ends up getting passed to this constructor when ngx-bootstrap is loading dynamically.
    // Else, fallback to the injector passed in.
    let validInjector: Injector;
    let requiresDigest = false;
    if (modalOptions) {
      if (!modalOptions.elementInjector) {
        console.error(`This upgraded component for '${name}' will not work properly` +
         ' inside of a modal without having the correct injector.' +
         ' This modal should be opened from ApModalService.showComponentModal() to fix this.');
      }
      validInjector = modalOptions.elementInjector || injector;
      requiresDigest = true;
    } else {
      validInjector = injector;
    }

    super(name, elementRef, validInjector);

    // If requiresDigest is true, then we need to trigger a digest cycle because of Issue 2 described above.
    // In the current state, whether the scope is provided globally for a module, or provided by this elementInjector,
    // both situations have this problem.
    // I guess theoretically this could be fixed at some point, and then we could take out this logic.
    if (requiresDigest) {
      // This logic is kinda based on from upgrade_component.ts, and downgrade_component.ts in angular,
      // where they are making this same call to make sure initial values
      // on the scope are detected.

      // I know this injector.get(token: any) call is deprecated, but it's what UpgradeComponent is calling as well,
      // so I don't really know a better way to do it.
      // todo: is UpgradeComponent different in later versions?? Check this after each angular update.
      // eslint-disable-next-line deprecation/deprecation
      const injScope: IScope = validInjector.get($SCOPE);
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      injScope.$evalAsync(() => {});
    }
  }

}
